import React, { useState, useEffect } from 'react';
import { 
  Users, Dumbbell, Plus, BarChart3, Eye, 
  Trash2, Edit, CheckCircle, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';

const CreateWorkoutDialog = ({ onCreate }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'strength',
    difficulty: 'intermediate',
    duration_minutes: 45,
    warmup: [],
    cooldown: [],
    exercises: []
  });
  const [currentExercise, setCurrentExercise] = useState({
    name: '',
    sets: 3,
    reps: '10',
    rest_seconds: 60,
    intensity: 'moderate',
    notes: '',
    muscle_groups: []
  });

  const addExercise = () => {
    if (!currentExercise.name) {
      toast.error('Please enter exercise name');
      return;
    }
    setFormData(prev => ({
      ...prev,
      exercises: [...prev.exercises, { ...currentExercise }]
    }));
    setCurrentExercise({
      name: '',
      sets: 3,
      reps: '10',
      rest_seconds: 60,
      intensity: 'moderate',
      notes: '',
      muscle_groups: []
    });
  };

  const removeExercise = (idx) => {
    setFormData(prev => ({
      ...prev,
      exercises: prev.exercises.filter((_, i) => i !== idx)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Please enter workout name');
      return;
    }
    if (formData.exercises.length === 0) {
      toast.error('Please add at least one exercise');
      return;
    }

    setLoading(true);
    try {
      await onCreate(formData);
      setOpen(false);
      setFormData({
        name: '',
        description: '',
        type: 'strength',
        difficulty: 'intermediate',
        duration_minutes: 45,
        warmup: [],
        cooldown: [],
        exercises: []
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="hpme-btn-primary flex items-center gap-2" data-testid="create-workout-btn">
          <Plus className="w-5 h-5" />
          Create Workout
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-surface border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-secondary font-bold text-2xl tracking-tight">
            CREATE <span className="text-primary">WORKOUT</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="hpme-label mb-2 block">WORKOUT NAME</Label>
              <Input
                className="hpme-input"
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Upper Body Power"
                data-testid="workout-name-input"
              />
            </div>
            <div className="col-span-2">
              <Label className="hpme-label mb-2 block">DESCRIPTION</Label>
              <Textarea
                className="hpme-input min-h-[80px]"
                value={formData.description}
                onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Describe your workout..."
              />
            </div>
            <div>
              <Label className="hpme-label mb-2 block">TYPE</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData(p => ({ ...p, type: v }))}>
                <SelectTrigger className="hpme-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="strength">Strength</SelectItem>
                  <SelectItem value="hypertrophy">Hypertrophy</SelectItem>
                  <SelectItem value="endurance">Endurance</SelectItem>
                  <SelectItem value="crossfit">CrossFit</SelectItem>
                  <SelectItem value="hyrox">HYROX</SelectItem>
                  <SelectItem value="mobility">Mobility</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="hpme-label mb-2 block">DIFFICULTY</Label>
              <Select value={formData.difficulty} onValueChange={(v) => setFormData(p => ({ ...p, difficulty: v }))}>
                <SelectTrigger className="hpme-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="hpme-label mb-2 block">DURATION (min)</Label>
              <Input
                type="number"
                className="hpme-input"
                value={formData.duration_minutes}
                onChange={(e) => setFormData(p => ({ ...p, duration_minutes: parseInt(e.target.value) || 45 }))}
              />
            </div>
          </div>

          {/* Add Exercise */}
          <div className="border border-zinc-800 p-4 rounded-sm">
            <h4 className="font-secondary font-bold mb-4">ADD EXERCISE</h4>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-3 md:col-span-2">
                <Input
                  className="hpme-input"
                  placeholder="Exercise name"
                  value={currentExercise.name}
                  onChange={(e) => setCurrentExercise(p => ({ ...p, name: e.target.value }))}
                  data-testid="exercise-name-input"
                />
              </div>
              <Input
                type="number"
                className="hpme-input"
                placeholder="Sets"
                value={currentExercise.sets}
                onChange={(e) => setCurrentExercise(p => ({ ...p, sets: parseInt(e.target.value) || 3 }))}
              />
              <Input
                className="hpme-input"
                placeholder="Reps"
                value={currentExercise.reps}
                onChange={(e) => setCurrentExercise(p => ({ ...p, reps: e.target.value }))}
              />
              <Input
                type="number"
                className="hpme-input"
                placeholder="Rest (sec)"
                value={currentExercise.rest_seconds}
                onChange={(e) => setCurrentExercise(p => ({ ...p, rest_seconds: parseInt(e.target.value) || 60 }))}
              />
              <Input
                className="hpme-input"
                placeholder="Notes"
                value={currentExercise.notes}
                onChange={(e) => setCurrentExercise(p => ({ ...p, notes: e.target.value }))}
              />
            </div>
            <Button 
              className="hpme-btn-secondary mt-3" 
              onClick={addExercise}
              data-testid="add-exercise-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Exercise
            </Button>
          </div>

          {/* Exercise List */}
          {formData.exercises.length > 0 && (
            <div>
              <h4 className="hpme-label mb-3">EXERCISES ({formData.exercises.length})</h4>
              <div className="space-y-2">
                {formData.exercises.map((ex, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-zinc-800/50 p-3 rounded-sm">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary text-black flex items-center justify-center text-sm font-bold">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-semibold">{ex.name}</p>
                        <p className="text-xs text-zinc-500">{ex.sets} × {ex.reps} | Rest: {ex.rest_seconds}s</p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeExercise(idx)}
                      className="text-zinc-500 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button 
            className="hpme-btn-primary w-full" 
            onClick={handleSubmit}
            disabled={loading}
            data-testid="save-workout-btn"
          >
            {loading ? 'Creating...' : 'Create Workout'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CoachPortal = () => {
  const { user } = useAuth();
  const [workouts, setWorkouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [workoutsRes, statsRes] = await Promise.all([
        api.getCoachWorkouts(),
        api.getCoachStats()
      ]);
      setWorkouts(workoutsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load coach data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkout = async (formData) => {
    try {
      const response = await api.createWorkout(formData);
      setWorkouts([response.data, ...workouts]);
      toast.success('Workout created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create workout');
      throw error;
    }
  };

  const handleDeleteWorkout = async (workoutId) => {
    try {
      await api.deleteWorkout(workoutId);
      setWorkouts(workouts.filter(w => w.id !== workoutId));
      toast.success('Workout deleted');
    } catch (error) {
      toast.error('Failed to delete workout');
    }
  };

  if (user?.role !== 'coach') {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Users className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
            <h2 className="font-secondary font-bold text-2xl mb-2">COACH ACCESS REQUIRED</h2>
            <p className="text-zinc-400">This page is only available for coach accounts</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-secondary font-bold tracking-tight">
              COACH <span className="text-primary">PORTAL</span>
            </h1>
            <p className="text-zinc-400 mt-1">Create and manage workout plans for your athletes</p>
          </div>
          <CreateWorkoutDialog onCreate={handleCreateWorkout} />
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-4">
            <div className="hpme-card p-5">
              <div className="hpme-card-corner" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                  <Dumbbell className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="hpme-number text-3xl text-primary">{stats.total_workouts_created}</p>
                  <p className="text-xs text-zinc-500 uppercase">Workouts Created</p>
                </div>
              </div>
            </div>
            <div className="hpme-card p-5">
              <div className="hpme-card-corner" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent-blue/20 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-accent-blue" />
                </div>
                <div>
                  <p className="hpme-number text-3xl text-accent-blue">{stats.total_sessions_using_workouts}</p>
                  <p className="text-xs text-zinc-500 uppercase">Times Used</p>
                </div>
              </div>
            </div>
            <div className="hpme-card p-5">
              <div className="hpme-card-corner" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="hpme-number text-3xl text-green-500">{stats.completed_sessions}</p>
                  <p className="text-xs text-zinc-500 uppercase">Completed</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Workouts List */}
        <div>
          <h2 className="font-secondary font-bold text-xl mb-4">YOUR WORKOUTS</h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Dumbbell className="w-8 h-8 text-primary animate-pulse" />
            </div>
          ) : workouts.length === 0 ? (
            <div className="hpme-card p-12 text-center">
              <Dumbbell className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
              <h3 className="font-secondary font-bold text-xl mb-2">NO WORKOUTS YET</h3>
              <p className="text-zinc-400 mb-6">Create your first workout plan for athletes</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {workouts.map(workout => (
                <div key={workout.id} className="hpme-card p-5">
                  <div className="hpme-card-corner" />
                  
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-secondary font-bold text-lg">{workout.name}</h3>
                      <p className="text-zinc-400 text-sm line-clamp-2">{workout.description}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-sm text-xs uppercase ${
                      workout.difficulty === 'beginner' ? 'bg-green-500/20 text-green-500' :
                      workout.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-500' :
                      'bg-red-500/20 text-red-500'
                    }`}>
                      {workout.difficulty}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-zinc-500 text-sm mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {workout.duration_minutes} min
                    </span>
                    <span className="flex items-center gap-1">
                      <Dumbbell className="w-4 h-4" />
                      {workout.exercises?.length || 0} exercises
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      variant="ghost" 
                      className="text-zinc-500 hover:text-red-400"
                      onClick={() => handleDeleteWorkout(workout.id)}
                      data-testid={`delete-coach-workout-${workout.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CoachPortal;
